import { Box, Divider, Stack, Typography, useTheme } from '@mui/material';
import { PieChart, useDrawingArea } from '@mui/x-charts';

import { Strings } from '@app/constants';
import { ILeaves } from '@app/modules/manager-dashboard';
import { StyledText } from './styles';
import { SvgColor } from '../svg-color';
import { DataNotFound } from '../page-404';

interface Props {
  leaveData: any;
  title: string;
  allowedDays?: number;
}
const TotalLeavesChart = ({ title, leaveData, allowedDays }: Props) => {
  const theme = useTheme();
  const colors = [
    theme.palette.quaternary.darkGreen,
    theme.palette.quaternary.blue,
    theme.palette.quaternary.darkBlue,
    theme.palette.info.darker,
  ];

  const barWithColors =
    leaveData?.map((item: any, index: number) => ({
      ...item,
      color: colors[index % colors.length],
    })) || [];

  const totalLeavesTaken = leaveData?.reduce(
    (acc: number, leave: { taken: number }) => acc + leave.taken,
    0
  );

  const series = [
    {
      innerRadius: 70,
      outerRadius: 100,
      id: 'series-1',
      data: barWithColors,
    },
  ];

  const legendData = leaveData?.map((dataItem: any, index: number) => ({
    value: dataItem.leaveType,
    total: dataItem.value,
    taken: dataItem.taken,
    color: colors[index],
  }));

  function PieCenterLabel({ children }: { children: React.ReactNode }) {
    const { width, height, left, top } = useDrawingArea();
    return (
      <StyledText x={left + width / 2} y={top + height / 3}>
        {children}
      </StyledText>
    );
  }

  const size = {
    width: 300,
    height: 200,
  };
  const LossOfPayLeaves = Math.max(totalLeavesTaken - (allowedDays ?? 0), 0);
  const totalAllowed =
    leaveData?.reduce((acc: number, item: any) => acc + Number(item.value), 0) ?? 0;
  const totalRemaining = Math.max(0, totalAllowed - (totalLeavesTaken ?? 0));
  const summaryLegendData = [
    {
      label: Strings.leave.totalAllow,
      value: totalAllowed,
    },
    {
      label: Strings.leave.totalTaken,
      value: totalLeavesTaken,
    },
    {
      label: Strings.leave.totalRemaining,
      value: totalRemaining,
    },
  ];

  return (
    <Stack p={1} sx={{ height: '100%' }}>
      <Stack p={1} direction={'row'} justifyContent={'space-between'}>
        <Typography variant="h6">{title}</Typography>
      </Stack>
      {leaveData && leaveData?.length > 0 ? (
        <Box
          sx={{
            height: '380px',
          }}
        >
          <Stack
            direction={'row'}
            sx={{
              width: '100%',
              margin: 0,
              padding: 0,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <PieChart
              series={series}
              slotProps={{
                legend: { hidden: true },
              }}
              sx={{
                margin: 0,
                padding: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginLeft: { md: '140px', xs: '100px' },
              }}
              {...size}
            >
              <PieCenterLabel>
                <tspan
                  fontWeight={'bolder'}
                  dx={'0'}
                  dy={'20px'}
                  style={{ display: 'flex', justifySelf: 'center' }}
                >
                  {totalLeavesTaken}
                </tspan>
                <tspan dy="20px" dx={'-60'} fill={theme.palette.grey[700]} fontSize={16}>
                  {Strings.employeeDashborad.leavesTaken}
                </tspan>
              </PieCenterLabel>
            </PieChart>
            <Stack
              sx={{
                backgroundImage: 'url(/assets/images/leaveCard.png)',
                backgroundSize: '100% 100%',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                height: 210,
                width: 150,
                mt: -6,
                mr: { xs: -2, md: 0 },
              }}
            >
              <Stack mt={{ md: 2, xs: 4 }} my={1} alignItems={'center'} fontSize={12}>
                {Strings.employeeDashborad.allowedLeaves}
              </Stack>
              {legendData?.map((legendItem: ILeaves) => (
                <Stack
                  key={legendItem.value}
                  sx={{
                    display: 'flex',
                    mb: { md: 0.7, xs: -0.4 },
                    borderRadius: '4px 4px 4px 4px',
                    my: 0.3,
                  }}
                >
                  <Stack
                    sx={{
                      height: '13px',
                      flexDirection: 'row',
                      borderRadius: '3px 3px 3px 3px',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Stack
                      sx={{
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontWeight: 600,
                        fontSize: { md: '10px', xs: '7px' },
                        borderRadius: '3px 3px 3px 3px',
                        color: legendItem.color,
                        p: 0.2,
                        px: { md: 1, xs: 3 },
                      }}
                    >
                      {`${legendItem?.value as string}:`}
                    </Stack>
                    <Stack
                      sx={{
                        width: '50px',
                        fontSize: '12px',
                        color: legendItem.color,
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderRadius: '0 3px 3px 0',
                        p: 0.2,
                      }}
                    >
                      {String(legendItem?.total)}
                    </Stack>
                  </Stack>
                </Stack>
              ))}
              <Stack justifyContent="space-between" direction={'row'}>
                <Stack direction={'row'} width={{ xs: 120 }}>
                  <Stack ml={{ xs: 0.5 }}>
                    <SvgColor
                      src={`/assets/icons/card/lopLeaves.svg`}
                      color={theme.palette.error.main}
                    />
                  </Stack>
                  <Stack
                    color={theme.palette.error.main}
                    fontSize={{ md: 12, xs: 9 }}
                    mt={{ md: 0.5, xs: 0.8 }}
                  >
                    {Strings.employeeDashborad.lopLeaves}
                  </Stack>
                </Stack>
                <Stack
                  sx={{
                    width: '50px',
                    fontSize: '12px',
                    color: theme.palette.error.main,
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '0 3px 3px 0',
                    p: 0.2,
                  }}
                >
                  {LossOfPayLeaves}
                </Stack>
              </Stack>
              <Divider sx={{ ml: { xs: 2, md: 0 }, my: 1 }} />
              {summaryLegendData?.map((item: any) => (
                <Stack
                  key={item.label}
                  sx={{
                    display: 'flex',
                    mb: { md: 0.7, xs: -0.4 },
                    borderRadius: '4px',
                  }}
                >
                  <Stack
                    sx={{
                      height: '13px',
                      flexDirection: 'row',
                      borderRadius: '3px',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Stack
                      sx={{
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontWeight: 600,
                        fontSize: { md: '10px', xs: '7px' },
                        color: item.color,
                        p: 0.2,
                        px: { md: 1, xs: 3 },
                      }}
                    >
                      {item.label}:
                    </Stack>
                    <Stack
                      sx={{
                        width: '50px',
                        fontSize: '12px',
                        color: item.color,
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderRadius: '0 3px 3px 0',
                        p: 0.2,
                      }}
                    >
                      {item.value}
                    </Stack>
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </Stack>
          <Stack
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
            mt={3}
            gap={1.5}
          >
            {legendData?.map((legendItem: any, index: any) => (
              <Stack
                key={legendItem.value}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: legendItem.color,
                  marginBottom: 5,
                  borderRadius: '4px 4px 4px 4px',
                }}
              >
                <Stack
                  sx={{
                    height: '25px',
                    flexDirection: 'row',
                    borderRadius: '3px 3px 3px 3px',
                  }}
                >
                  <Stack
                    sx={{
                      backgroundColor: theme.palette.grey[300],
                      justifyContent: 'center',
                      alignItems: 'center',
                      fontWeight: 600,
                      fontSize: '10px',
                      borderRadius: '3px 3px 3px 3px',
                      color: legendItem.color,
                      p: 0.2,
                      px: 1,
                    }}
                  >
                    {Strings.leave.taken} {legendItem.value}
                  </Stack>
                  <Stack
                    sx={{
                      width: '50px',
                      backgroundColor: legendItem.color,
                      fontSize: '12px',
                      color: theme.palette.common.white,
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderRadius: '0 3px 3px 0',
                      p: 0.2,
                    }}
                  >
                    {String(legendItem?.taken)}
                  </Stack>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Box>
      ) : (
        <Stack sx={{ height: '100%', justifyContent: 'center' }}>
          <DataNotFound height={200} />
        </Stack>
      )}
    </Stack>
  );
};

export { TotalLeavesChart };
